import {
  collection, limit, onSnapshot, orderBy, query, Timestamp, where,
} from 'firebase/firestore';
import { useEffect } from 'react';

import { db } from '@/config';
import { InAppNotification } from '@/types/inAppNotification.types';
import { getTranslatedContent } from '@/utils/inAppNotification.utils';
import { isAlertSoundEnabled, isSystemNotificationEnabled } from '@/utils/notificationPreferences';
import { showLocalNotification } from '@/utils/webPush';

/**
 * Alerty (dźwięk + powiadomienie systemowe) dla powiadomień z bazy.
 *
 * JEDNO ŹRÓDŁO PRAWDY: `users/{uid}/notifications`. O tym, CZY coś jest powiadomieniem,
 * decyduje wyłącznie backend (guardy `internalOrder`, `unpaid`, docelowo role) — panel
 * ma to tylko zagrać i pokazać. Wcześniej nasłuchiwaliśmy równolegle surowych kolekcji
 * (`orders`, `tasks`, …) i reguły trzeba było pisać dwa razy; efektem był dzwonek przy
 * zamówieniu `unpaid`, którego nigdzie nie było widać.
 */

type AlertSound = 'new' | 'cancelled';

const SOUND_FILES: Record<AlertSound, string> = {
  new: '/sounds/notification.mp3',
  cancelled: '/notification-sound.mp3',
};

const SOUND_VOLUME: Record<AlertSound, number> = { new: 0.3, cancelled: 0.4 };

/** Dwa zdarzenia w tej samej sekundzie to jeden dzwonek — inaczej alert się zlewa w hałas. */
const SOUND_DEBOUNCE_MS = 3000;
/** Ile ostatnich powiadomień obserwujemy. Alert dotyczy świeżych, nie historii. */
const WATCH_LIMIT = 20;
/** Klucz roszczenia w localStorage — chroni przed dzwonkiem z każdej otwartej karty naraz. */
const CLAIM_PREFIX = 'hs.notifAlert.';
const CLAIM_TTL_MS = 60_000;

const audioCache: Partial<Record<AlertSound, HTMLAudioElement>> = {};
let lastSoundAt = 0;
let audioUnlocked = false;

const ensureAudio = (sound: AlertSound): HTMLAudioElement => {
  if (!audioCache[sound]) {
    const audio = new Audio(SOUND_FILES[sound]);
    audio.volume = SOUND_VOLUME[sound];
    audio.preload = 'auto';
    audioCache[sound] = audio;
  }
  return audioCache[sound]!;
};

/**
 * Odblokowanie dźwięku pierwszym kliknięciem w panelu.
 *
 * Przeglądarki puszczają `audio.play()` dopiero po JAKIEJKOLWIEK interakcji z domeną.
 * Recepcja potrafi zostawić otwarty panel bez dotykania go godzinami, a wtedy pierwsze
 * zamówienie trafiało w zablokowany autoplay i dzwonek po prostu nie grał. Odtwarzamy
 * więc raz, bezgłośnie, przy pierwszym kliknięciu — od tego momentu gra normalnie.
 */
const unlockAudio = () => {
  if (audioUnlocked) return;
  audioUnlocked = true;

  (Object.keys(SOUND_FILES) as AlertSound[]).forEach((sound) => {
    const audio = ensureAudio(sound);
    const { volume } = audio;
    audio.volume = 0;
    audio.play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = volume;
      })
      .catch(() => {
        audio.volume = volume;
      });
  });
};

if (typeof window !== 'undefined') {
  ['pointerdown', 'keydown'].forEach((event) => {
    window.addEventListener(event, unlockAudio, { once: true, passive: true });
  });
}

const playAlertSound = (sound: AlertSound) => {
  if (!isAlertSoundEnabled()) return;

  const now = Date.now();
  if (now - lastSoundAt < SOUND_DEBOUNCE_MS) return;
  lastSoundAt = now;

  const audio = ensureAudio(sound);
  try {
    audio.currentTime = 0;
  } catch {
    // Ścieżka jeszcze się nie załadowała — `play()` i tak zacznie od zera.
  }

  // Dźwięk gra też przy karcie w tle: recepcja przełącza się na PMS i ma usłyszeć
  // zamówienie. Odrzucenie obietnicy = polityka autoplay albo brak pliku — jedno i
  // drugie kończyło się dotąd ciszą bez śladu, więc zostawiamy ślad w konsoli.
  audio.play().catch((error) => {
    // eslint-disable-next-line no-console
    console.warn('[notifications] nie udało się odtworzyć dźwięku', error);
  });
};

/**
 * Roszczenie do alertu, wspólne dla kart tej samej przeglądarki.
 *
 * Bez tego pracownik z panelem otwartym w trzech kartach słyszy trzy dzwonki naraz —
 * wcześniej maskował to warunek „tylko aktywna karta", którego już nie ma.
 */
const claimAlert = (notificationId: string): boolean => {
  const key = `${CLAIM_PREFIX}${notificationId}`;
  try {
    const existing = localStorage.getItem(key);
    if (existing && Date.now() - Number(existing) < CLAIM_TTL_MS) return false;
    localStorage.setItem(key, String(Date.now()));

    // Sprzątanie po sobie — klucze alertów nie mogą rosnąć w nieskończoność.
    Object.keys(localStorage)
      .filter((entry) => entry.startsWith(CLAIM_PREFIX))
      .forEach((entry) => {
        const stamp = Number(localStorage.getItem(entry));
        if (!stamp || Date.now() - stamp > CLAIM_TTL_MS) localStorage.removeItem(entry);
      });

    return true;
  } catch {
    // Brak localStorage (tryb prywatny) — lepiej zadzwonić dwa razy niż wcale.
    return true;
  }
};

/**
 * `tag` dymka systemowego — MUSI być ten sam, którym oznacza push backend
 * (`createNotificationPayload`: `orderId` → `reservationId` → `taskId`).
 *
 * Na tym tagu opiera się całe odróżnianie „to samo zdarzenie" po stronie przeglądarki:
 * zgodne tagi = dymek z pusha PODMIENIA ten z karty. Tag brany z id dokumentu
 * powiadomienia nigdy nie pasował do pusha, więc o jednej zmianie statusu recepcja
 * dostawała dwa dymki. Gdy zdarzenie nie ma żadnego z tych identyfikatorów, zostaje
 * id dokumentu — lepiej dwa dymki niż zlepienie dwóch różnych zdarzeń w jeden.
 */
const notificationTag = (notification: InAppNotification): string => {
  const metadata = notification.metadata || {};
  const source = metadata.orderId || metadata.reservationId || metadata.taskId;
  return typeof source === 'string' && source ? source : notification.id;
};

/** Powiadomienie systemowe pokazujemy tylko, gdy karty nie widać — inaczej dubluje UI panelu. */
const showSystemNotification = (notification: InAppNotification, title: string, body: string) => {
  if (!isSystemNotificationEnabled()) return;
  // Dymek tylko przy karcie w tle — przy otwartym panelu dubluje to, co widać w dzwonku.
  if (!document.hidden) return;

  showLocalNotification(title, { body, tag: notificationTag(notification), icon: '/logo192.png' })
    .catch(() => undefined);
};

const soundFor = (notification: InAppNotification): AlertSound => {
  const event = String(notification.metadata?.event || '');
  return event.endsWith('.cancelled') ? 'cancelled' : 'new';
};

export const useNotificationAlerts = (
  userId: string | undefined,
  hotelId: string | null | undefined,
  language = 'pl',
) => {
  useEffect(() => {
    if (!userId || !hotelId) return undefined;

    // Alert dotyczy tego, co przyszło PO wejściu do panelu. Bez tego odcięcia każde
    // wejście odtwarzałoby historię, a po zmianie hotelu — powiadomienia z drugiego.
    const mountedAt = Timestamp.now();
    const alerted = new Set<string>();

    const notificationsQuery = query(
      collection(db, 'users', userId, 'notifications'),
      where('hotelId', '==', hotelId),
      orderBy('createdAt', 'desc'),
      limit(WATCH_LIMIT),
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        if (snapshot.metadata.hasPendingWrites) return;

        snapshot.docChanges().forEach((change) => {
          /* `added` zamiast porównywania rozmiaru snapshotu: licznik gubił zdarzenie,
             gdy w tej samej chwili ktoś oznaczył inne powiadomienie jako przeczytane. */
          if (change.type !== 'added') return;

          const notification = { id: change.doc.id, ...change.doc.data() } as InAppNotification;
          const createdAt = notification.createdAt as Timestamp | undefined;
          if (!createdAt?.toMillis || createdAt.toMillis() <= mountedAt.toMillis()) return;
          if (alerted.has(notification.id)) return;
          alerted.add(notification.id);

          if (!claimAlert(notification.id)) return;

          playAlertSound(soundFor(notification));
          showSystemNotification(
            notification,
            getTranslatedContent(notification.title, language),
            getTranslatedContent(notification.description, language),
          );
        });
      },
      (error) => {
        /* Cichy błąd listenera = brak dzwonka BEZ żadnego objawu (np. gdy zabraknie
           indeksu złożonego `hotelId + createdAt`). Wypisujemy go, żeby dało się to
           odróżnić od „po prostu nic nie przyszło". */
        // eslint-disable-next-line no-console
        console.error('[notifications] listener powiadomień padł', error);
      },
    );

    return () => unsubscribe();
  }, [userId, hotelId, language]);
};
